
import React, { useState, useEffect, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { redirectAfterAuth, withReturnTo } from '../utils/returnTo';
import { APP_CONFIG } from '../config/appConfig';
import ShopifyAuth from '../components/shared/ShopifyAuth';
import { shopifyAPI } from '../services/api';

const isShopifyBuild = APP_CONFIG.billingProvider === 'shopify';

const Login = () => {
    const { login, googleLogin } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
    const [bouncingToShopify, setBouncingToShopify] = useState(false);
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const navigate = useNavigate();
    const recaptchaSiteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

    // App Store install auto-OAuth: Shopify redirects merchants to
    // shop.prodagi.ai/?shop=<store>.myshopify.com&hmac=…&host=…&timestamp=…
    // after install. Forward `shop` straight to /api/auth/shopify/url so the
    // marketing/login page never flashes. Stripe build skips entirely.
    useEffect(() => {
        if (!isShopifyBuild) return;
        const shop = new URLSearchParams(window.location.search).get('shop');
        if (!shop || !shop.trim()) return;
        setBouncingToShopify(true);
        shopifyAPI.getAuthUrl(shop.trim()).catch((err: unknown) => {
            const msg =
                (err as { response?: { data?: { detail?: string } } })
                    ?.response?.data?.detail ||
                'Failed to start Shopify connection. Please try again.';
            setError(msg);
            setBouncingToShopify(false);
        });
    }, []);

    // Initialize Google Sign-In
    useEffect(() => {
        if (isShopifyBuild) return; // Shopify build has no Google sign-in
        const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
        if (!clientId || clientId === 'your_google_client_id_here') return;

        const initializeGoogleSignIn = () => {
            if (typeof window.google !== 'undefined') {
                window.google.accounts.id.initialize({
                    client_id: clientId,
                    callback: handleGoogleResponse,
                });
                window.google.accounts.id.renderButton(
                    document.getElementById('googleSignInButton'),
                    { theme: 'outline', size: 'large', width: '100%', text: 'continue_with' }
                );
            }
        };

        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = initializeGoogleSignIn;
        document.body.appendChild(script);

        return () => {
            if (document.body.contains(script)) document.body.removeChild(script);
        };
    }, []);

    const handleGoogleResponse = async (response: any) => {
        try {
            setLoading(true);
            setError('');
            const base64Url = response.credential.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''));
            const userData = JSON.parse(jsonPayload);

            await googleLogin({
                credential: response.credential,
                name: userData.name,
                email: userData.email,
                sub: userData.sub,
                picture: userData.picture,
                given_name: userData.given_name,
                family_name: userData.family_name,
            });
            redirectAfterAuth(navigate);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Google sign-in failed');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await login(email, password, recaptchaToken || undefined);
            redirectAfterAuth(navigate);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Invalid email or password');
            recaptchaRef.current?.reset();
            setRecaptchaToken(null);
        } finally {
            setLoading(false);
        }
    };

    if (bouncingToShopify) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 text-center">
                <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100">
                    Connecting to Shopify…
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Hold tight while we redirect you to authorize the install.
                </p>
            </div>
        );
    }

    return (
        <div className="min-h-screen relative flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8 overflow-hidden">
            {/* Background Effects */}
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-primary-900/10 to-transparent pointer-events-none"></div>
            <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-primary/20 rounded-full blur-[100px] animate-pulse-glow pointer-events-none" />

            <div className="max-w-md w-full space-y-8 relative z-10 glass p-10 rounded-3xl shadow-2xl">
                <div className="text-center">
                    <a href="https://askprodigy.ai" rel="noopener" className="inline-flex items-center space-x-2 mb-6 group">
                        <img src="/logo-purple.png" alt="AskProdigy.ai" className="h-14 w-auto group-hover:scale-110 transition-transform dark:hidden" />
                        <img src="/logo-white.png" alt="AskProdigy.ai" className="h-14 w-auto group-hover:scale-110 transition-transform hidden dark:block" />
                    </a>
                    <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white mb-2">Welcome back</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Sign in to access your AI agents.
                    </p>
                </div>

                {isShopifyBuild ? (
                  <ShopifyAuth mode="login" />
                ) : (
                <>
                {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-xl text-sm text-center">
                        {error}
                    </div>
                )}

                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Email address</label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="input-field"
                                placeholder="you@company.com"
                            />
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
                                <Link to="/forgot-password" className="text-xs font-medium text-primary hover:text-primary-600">Forgot password?</Link>
                            </div>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    required
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="input-field pr-11"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(v => !v)}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                    </div>

                    {recaptchaSiteKey && (
                        <div className="flex justify-center">
                            <ReCAPTCHA
                                ref={recaptchaRef}
                                sitekey={recaptchaSiteKey}
                                onChange={(token) => setRecaptchaToken(token)}
                                onExpired={() => setRecaptchaToken(null)}
                            />
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading || (!!recaptchaSiteKey && !recaptchaToken)}
                        className="w-full btn-primary flex justify-center py-3"
                    >
                        {loading ? 'Signing in...' : 'Sign in'}
                    </button>

                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-300 dark:border-gray-700"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">Or continue with</span>
                        </div>
                    </div>

                    <div id="googleSignInButton" className="flex justify-center h-10"></div>
                </form>

                <div className="text-center text-sm">
                    <p className="text-gray-600 dark:text-gray-400">
                        Don't have an account?{' '}
                        <Link to={withReturnTo('/register')} className="font-medium text-primary hover:text-primary-600 transition-colors">
                            Sign up today
                        </Link>
                    </p>
                </div>
                </>
                )}
            </div>
        </div>
    );
};

export default Login;
